import type { Process, ProcessSummary } from "../../api/types";
import type { ProcessInstance } from "./processGraphModel";
import { processPeople } from "./processPeople";

export type SearchField =
  | "name"
  | "description"
  | "step"
  | "app"
  | "person"
  | "group"
  | "session"
  | "object";
export type SearchMatch = { field: SearchField; value: string; score: number };
export const normalizeSearch = (text: string) =>
  text
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/** A field with its normalised text, so repeated searches over it skip re-normalising. */
type NormalizedField = [field: SearchField, value: string, haystack: string];

const bestMatch = (needle: string, fields: NormalizedField[]): SearchMatch | null => {
  const tokens = needle.split(" ");
  let best: SearchMatch | null = null;
  for (const [field, value, haystack] of fields) {
    if (!tokens.every(token => haystack.includes(token))) continue;
    const score =
      (haystack === needle ? 100 : haystack.includes(needle) ? 60 : 30) +
      (field === "name" ? 10 : field === "step" ? 5 : 0);
    if (!best || score > best.score) best = { field, value, score };
  }
  return best;
};

export function matchFields(
  query: string,
  fields: Array<[SearchField, string]>,
): SearchMatch | null {
  const needle = normalizeSearch(query);
  if (!needle) return null;
  return bestMatch(
    needle,
    fields.map(([field, value]) => [field, value, normalizeSearch(value)]),
  );
}

export function searchProcesses(
  processes: ProcessSummary[],
  query: string,
  people: Record<string, string> = {},
  favourites: string[] = [],
  recent: string[] = [],
  groupName: (process: ProcessSummary) => string = () => "",
) {
  return processes
    .map(process => ({
      process,
      match: matchFields(query, [
        ["name", process.name],
        ...(process.aliases ?? []).map(value => ["name", value] as [SearchField, string]),
        ["description", process.description ?? ""],
        ...(process.stepNames ?? []).map(value => ["step", value] as [SearchField, string]),
        ...(process.apps ?? []).map(value => ["app", value] as [SearchField, string]),
        ...processPeople(process).map(
          person => ["person", people[person.userId] ?? ""] as [SearchField, string],
        ),
        ["group", groupName(process)],
      ]),
    }))
    .filter(row => !normalizeSearch(query) || row.match)
    .sort(
      (a, b) =>
        (b.match?.score ?? 0) - (a.match?.score ?? 0) ||
        Number(favourites.includes(b.process.slug)) - Number(favourites.includes(a.process.slug)) ||
        (recent.indexOf(a.process.slug) < 0
          ? Number.POSITIVE_INFINITY
          : recent.indexOf(a.process.slug)) -
          (recent.indexOf(b.process.slug) < 0
            ? Number.POSITIVE_INFINITY
            : recent.indexOf(b.process.slug)) ||
        b.process.frequency - a.process.frequency ||
        a.process.name.localeCompare(b.process.name),
    );
}

export const runApps = (run: ProcessInstance) =>
  [
    ...new Set(
      run.steps.flatMap(step =>
        Object.entries(step.appSeconds ?? {})
          .filter(([, seconds]) => seconds > 0)
          .map(([app]) => app),
      ),
    ),
  ].sort();

// Case search runs over every case per keystroke: each case's fields are gathered and normalised
// once per process graph, leaving only the query to normalise as it is typed.
const nodeNamesByGraph = new WeakMap<Process["nodes"], Map<string, string>>();
const runFieldsCache = new WeakMap<
  ProcessInstance,
  { nodes: Process["nodes"]; fields: NormalizedField[] }
>();

const runFields = (nodes: Process["nodes"], run: ProcessInstance): NormalizedField[] => {
  const cached = runFieldsCache.get(run);
  if (cached?.nodes === nodes) return cached.fields;
  let nodeNames = nodeNamesByGraph.get(nodes);
  if (!nodeNames) {
    nodeNames = new Map(nodes.map(node => [node.id, node.name]));
    nodeNamesByGraph.set(nodes, nodeNames);
  }
  const names = nodeNames;
  const raw: Array<[SearchField, string]> = [
    ["description", run.description ?? ""],
    ["person", run.source.userName ?? ""],
    ["session", run.source.recordingName ?? ""],
    ...runApps(run).map(value => ["app", value] as [SearchField, string]),
    ...run.steps.flatMap(step => [
      ["step", names.get(step.nodeId) ?? ""] as [SearchField, string],
      ["description", step.description] as [SearchField, string],
      ...step.objects.map(object => ["object", object.object] as [SearchField, string]),
    ]),
  ];
  const fields = raw.map(
    ([field, value]): NormalizedField => [field, value, normalizeSearch(value)],
  );
  runFieldsCache.set(run, { nodes, fields });
  return fields;
};

export function matchRun(
  process: Pick<Process, "nodes">,
  run: ProcessInstance,
  query: string,
): SearchMatch | null {
  const needle = normalizeSearch(query);
  return needle ? bestMatch(needle, runFields(process.nodes, run)) : null;
}
